/**
 * RBAC-3.1 — Settings → Roles & Permissions (UX-DR1, UX-DR2, UX-DR17).
 *
 * Its own route, `/app/settings/roles`, gated on `roles.administer` via `rolesScreenAccess()`, never
 * on `manage_roles` and never behind `SettingsGuard` (which redirects; AC 2 asks for a calm surface).
 * Why both, in `lib/accountRoles.ts`. The database refuses the same callers (`20261101a9`).
 *
 * Two panes: roles left with holder counts, the account's own above the Joules ones; the selected
 * role right, in RBAC-3.5's `PermissionEditor`. A Joules role renders the same editor read only, with
 * a line saying what to do instead. Duplicate is offered on EVERY role (RBAC-3.3's dialog). RBAC-3.2
 * adds New role above the account's own roles, and Rename and Delete on those roles only: a Joules
 * role is read only (FR-5). RBAC-3.4: a Joules role offers Adopt beside Duplicate, never Edit, and
 * says what each does instead of a dead end (FR-2c / UX-DR9), except the two privileged ones; an
 * account role made by Adopt offers Reverse while the copy is unedited (FR-2b). Saving is RBAC-3.6's,
 * through the editor's Review changes dialog, which then drops the role's draft and refetches the list.
 * RBAC-3.8 adds search across both groups and folding groups (`RoleListNav`, `lib/roleListView.ts`).
 *
 * RBAC-3.7 adds the By capability tab (`?view=capability&cap=<id>`, `CapabilityLookup`). The page
 * holds ONE draft per role, shared by both tabs, so a toggle there and a control in the editor are
 * the same edit and RBAC-3.6 has one draft per role to put through its diff.
 *
 * RBAC-8.1 adds History on the account's own roles (`RoleHistoryDialog`). Restoring a version opens
 * RBAC-3.6's Review dialog in restore mode, so it is the same diff, count and confirm as a save.
 *
 * RBAC-8.2 adds the Recent changes tab (`?view=changes`, `RoleChangeLog`): the account-level log,
 * filtered on date range, actor, who or what was affected, and kind of change.
 */
import { useMemo, useState, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronRight, Copy, History, Info, Loader2, Pencil, Plus, Search, ShieldAlert, Trash2, Undo2, UsersRound } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { AdoptRoleDialog } from '@/components/settings/components/roles/AdoptRoleDialog';
import { DeleteRoleDialog } from '@/components/settings/components/roles/DeleteRoleDialog';
import { DuplicateRoleDialog } from '@/components/settings/components/roles/DuplicateRoleDialog';
import { RevertAdoptionDialog } from '@/components/settings/components/roles/RevertAdoptionDialog';
import { RoleDetailsDialog } from '@/components/settings/components/roles/RoleDetailsDialog';
import { ReviewChangesDialog } from '@/components/settings/components/roles/ReviewChangesDialog';
import { RoleHistoryDialog, roleHistoryQueryKey } from '@/components/settings/roles/RoleHistoryDialog';
import { usePermissions } from '@/contexts/PermissionContext';
import { useTenant } from '@/contexts/TenantContext';
import { cn } from '@/lib/utils';
import { CapabilityLookup } from '@/components/settings/roles/CapabilityLookup';
import { RoleChangeLog, roleAuditQueryKey } from '@/components/settings/roles/RoleChangeLog';
import { PermissionEditor } from '@/components/settings/roles/PermissionEditor';
import {
  groupAccountRoles,
  holderLabel,
  roleKindLabel,
  rolesScreenAccess,
  selectedRole,
  type AccountRole,
  type GroupedRoles,
} from '@/lib/accountRoles';
import {
  filterRoleGroups,
  groupCountLabel,
  groupOpen,
  noMatchMessage,
  type RoleGroupKey,
} from '@/lib/roleListView';
import { canOfferAdopt, joulesRoleGuidance, type RoleAdoptionStatus } from '@/lib/roleAdoption';
import { fetchAccountRoles } from '@/services/accountRolesService';
import { fetchRoleAdoptionStatus } from '@/services/roleAdoptionService';
import { RolesNoAccess } from '@/components/settings/roles/RolesNoAccess';

// RBAC-5.3: the blocked surface moved to its own module; re-exported for existing importers.
export { RolesNoAccess };

function PageHeader() {
  return (
    <div className="flex justify-between items-center mb-6">
      <h1 className="text-2xl font-bold text-foreground">
        <span className="text-muted-foreground font-normal">Settings</span>
        <span className="text-muted-foreground font-normal mx-2">/</span>
        Roles &amp; Permissions
      </h1>
    </div>
  );
}

function RoleRow({ role, active, onSelect }: { role: AccountRole; active: boolean; onSelect: () => void }) {
  return (
    <button
      type="button"
      onClick={onSelect}
      aria-current={active ? 'true' : undefined}
      className={cn(
        'flex w-full items-center justify-between gap-3 rounded-md px-3 py-2 text-left text-sm transition-colors',
        active ? 'bg-primary/10 text-foreground font-medium' : 'text-foreground hover:bg-muted',
      )}
    >
      <span className="truncate">{role.name}</span>
      <span
        className="shrink-0 tabular-nums text-xs text-muted-foreground"
        aria-label={holderLabel(role.holderCount)}
      >
        {role.holderCount}
      </span>
    </button>
  );
}

/** RBAC-3.8: a group header that folds its rows, carrying its count ("20", or "3 of 20" searching). */
function GroupCaption({
  children,
  note,
  count,
  open,
  onToggle,
  controls,
  action,
}: {
  children: string;
  note?: string;
  count: string;
  open: boolean;
  onToggle: () => void;
  controls: string;
  action?: ReactNode;
}) {
  return (
    <div className="flex min-w-0 items-center justify-between gap-2 px-1 pb-1 pt-3">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        aria-controls={controls}
        className="flex min-w-0 items-center gap-1 rounded px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground hover:text-foreground"
      >
        <ChevronRight className={cn('h-3.5 w-3.5 shrink-0 transition-transform', open && 'rotate-90')} />
        <span className="truncate">{children}</span>
        <span className="shrink-0 font-normal tabular-nums normal-case tracking-normal">· {count}</span>
        {note && <span className="shrink-0 font-normal normal-case tracking-normal">· {note}</span>}
      </button>
      {action}
    </div>
  );
}

/**
 * RBAC-3.8 (FR-17c): search across both groups, the account's own roles above the Joules ones, each
 * group folding; the Joules group starts folded once the account has many of its own. The rules are
 * `lib/roleListView.ts`; this only renders them.
 */
function RoleListNav({
  groups,
  current,
  onSelect,
  onNew,
}: {
  groups: GroupedRoles;
  current: AccountRole | null;
  onSelect: (r: AccountRole) => void;
  onNew: () => void;
}) {
  const [query, setQuery] = useState('');
  const [toggled, setToggled] = useState<Partial<Record<RoleGroupKey, boolean>>>({});
  const view = filterRoleGroups(groups, query);
  const open = (group: RoleGroupKey) =>
    groupOpen({
      group,
      ownCount: groups.own.length,
      selectedInGroup: !!current && current.isGlobal === (group === 'joules'),
      searching: view.searching,
      toggled: toggled[group],
    });
  const toggle = (group: RoleGroupKey) => setToggled((t) => ({ ...t, [group]: !open(group) }));
  const nothing = view.searching && view.own.length === 0 && view.joules.length === 0;

  const rows = (list: AccountRole[]) =>
    list.map((r) => <RoleRow key={r.id} role={r} active={current?.id === r.id} onSelect={() => onSelect(r)} />);

  return (
    <nav
      aria-label="Roles"
      className="min-w-0 overflow-x-hidden border-b border-border p-2 md:max-h-[calc(100vh-12rem)] md:overflow-y-auto md:border-b-0 md:border-r"
    >
      <div className="relative px-1 pb-1">
        <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => e.key === 'Escape' && setQuery('')}
          placeholder="Search roles"
          aria-label="Search roles"
          className="h-9 pl-8"
        />
      </div>
      {nothing && (
        <div className="space-y-2 px-3 py-3 text-sm text-muted-foreground">
          <p>{noMatchMessage(query)}</p>
          <Button variant="outline" size="sm" onClick={() => setQuery('')}>
            Clear search
          </Button>
        </div>
      )}
      {!nothing && (
        <>
          <GroupCaption
            count={groupCountLabel(view.own.length, view.ownTotal, view.searching)}
            open={open('own')}
            onToggle={() => toggle('own')}
            controls="roles-own"
            action={
              <Button variant="ghost" size="sm" className="h-6 shrink-0 px-2" onClick={onNew}>
                <Plus className="mr-1 h-3.5 w-3.5" />
                New role
              </Button>
            }
          >
            Your roles
          </GroupCaption>
          <div id="roles-own" hidden={!open('own')}>
            {groups.own.length === 0 ? (
              <p className="px-3 py-2 text-sm text-muted-foreground">This account has no roles of its own yet.</p>
            ) : view.own.length === 0 ? (
              <p className="px-3 py-2 text-sm text-muted-foreground">None of your roles match.</p>
            ) : (
              rows(view.own)
            )}
          </div>
          <GroupCaption
            note="read only"
            count={groupCountLabel(view.joules.length, view.joulesTotal, view.searching)}
            open={open('joules')}
            onToggle={() => toggle('joules')}
            controls="roles-joules"
          >
            Joules roles
          </GroupCaption>
          <div id="roles-joules" hidden={!open('joules')}>
            {view.joules.length === 0 ? (
              <p className="px-3 py-2 text-sm text-muted-foreground">No Joules roles match.</p>
            ) : (
              rows(view.joules)
            )}
          </div>
        </>
      )}
    </nav>
  );
}

interface RoleActions {
  onDuplicate: () => void;
  onRename: () => void;
  onDelete: () => void;
  onAdopt: () => void;
  onRevert: () => void;
}

type Adopted = Extract<RoleAdoptionStatus, { adopted: true }>;

/** FR-2b, on an account role made by Adopt: Reverse while unedited, otherwise why not. */
function AdoptionNote({ status, onRevert }: { status: Adopted; onRevert: () => void }) {
  return (
    <div className="flex gap-3 rounded-md border border-border bg-muted/50 p-3 text-sm text-foreground">
      <Info className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
      <div className="min-w-0 flex-1 space-y-2">
        <p>
          Adopted from the Joules role {status.sourceName}
          {status.movedCount > 0 && `, moving ${status.movedCount === 1 ? '1 person' : `${status.movedCount} people`} onto it`}.
        </p>
        {status.reversible ? (
          <Button variant="outline" size="sm" onClick={onRevert}>
            <Undo2 className="mr-1.5 h-4 w-4" />
            Reverse adoption
          </Button>
        ) : (
          <p className="text-muted-foreground">It can no longer be reversed. {status.blockers.join(' ')}</p>
        )}
      </div>
    </div>
  );
}

function RoleSummary({
  role,
  adoption,
  onDuplicate,
  onRename,
  onDelete,
  onAdopt,
  onRevert,
  draft,
  onDraftChange,
  onSaved,
}: {
  role: AccountRole;
  adoption: Adopted | null;
  draft: readonly string[];
  onDraftChange: (next: string[] | null) => void;
  onSaved: () => void;
} & RoleActions) {
  const overrides = role.permissions.includes('system.override');
  const guidance = joulesRoleGuidance(role);
  const queryClient = useQueryClient();
  // RBAC-8.1: History, and the version being reviewed for restore.
  const [showHistory, setShowHistory] = useState(false);
  const [restoring, setRestoring] = useState<{ versionId: number; when: string } | null>(null);
  const saved = new Set(role.permissions);
  const hasUnsavedChanges = draft.length !== saved.size || draft.some((k) => !saved.has(k));

  return (
    <div className="space-y-5">
      <div className="space-y-1">
        <div className="flex items-start justify-between gap-3">
          <h2 className="text-xl font-semibold text-foreground">{role.name}</h2>
          <div className="flex shrink-0 flex-wrap justify-end gap-2">
            {/* RBAC-3.2: own roles only. A Joules role is read only (FR-5); Duplicate is its way in. */}
            {!role.isGlobal && (
              <>
                <Button variant="outline" size="sm" onClick={() => setShowHistory(true)}>
                  <History className="mr-1.5 h-4 w-4" />
                  History
                </Button>
                <Button variant="outline" size="sm" onClick={onRename}>
                  <Pencil className="mr-1.5 h-4 w-4" />
                  Rename
                </Button>
                <Button variant="outline" size="sm" onClick={onDelete}>
                  <Trash2 className="mr-1.5 h-4 w-4" />
                  Delete
                </Button>
              </>
            )}
            {/* RBAC-3.4: Adopt on Joules roles, never Edit (FR-5). The server refuses the privileged two too. */}
            {canOfferAdopt(role) && (
              <Button size="sm" onClick={onAdopt}>
                <UsersRound className="mr-1.5 h-4 w-4" />
                Adopt
              </Button>
            )}
            {/* RBAC-3.3: offered on every role, Joules and own alike. The dialog names what it drops. */}
            <Button variant="outline" size="sm" onClick={onDuplicate}>
              <Copy className="mr-1.5 h-4 w-4" />
              Duplicate
            </Button>
          </div>
        </div>
        <p className="text-sm text-muted-foreground">
          {roleKindLabel(role)}
          {' · '}
          {holderLabel(role.holderCount)}
        </p>
        {role.description && <p className="text-sm text-muted-foreground">{role.description}</p>}
      </div>

      {overrides && (
        <div className="flex gap-3 rounded-md border border-border bg-muted/50 p-3 text-sm text-foreground">
          <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
          <span>This role overrides every permission in the product, whatever is listed below.</span>
        </div>
      )}

      {/* FR-2c: a Joules role says what to do next instead of a read-only form with no exit. */}
      {guidance && (
        <div className="flex gap-3 rounded-md border border-border bg-muted/50 p-3 text-sm text-foreground">
          <Info className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
          <div className="space-y-1">
            <p className="font-medium">{guidance.heading}</p>
            {guidance.lines.map((line) => (
              <p key={line} className="text-muted-foreground">{line}</p>
            ))}
          </div>
        </div>
      )}

      {adoption && <AdoptionNote status={adoption} onRevert={onRevert} />}

      <PermissionEditor
        key={role.id}
        roleId={role.id}
        onSaved={onSaved}
        permissions={role.permissions}
        draft={draft}
        onDraftChange={onDraftChange}
        readOnly={role.isGlobal}
      />

      {!role.isGlobal && (
        <>
          <RoleHistoryDialog
            open={showHistory}
            roleId={role.id}
            hasUnsavedChanges={hasUnsavedChanges}
            onOpenChange={setShowHistory}
            onRestore={(v) => {
              setShowHistory(false);
              setRestoring(v);
            }}
          />
          <ReviewChangesDialog
            open={restoring !== null}
            roleId={role.id}
            base={role.permissions}
            draft={role.permissions}
            restore={restoring}
            onOpenChange={(o) => !o && setRestoring(null)}
            onSaved={() => {
              onDraftChange(null);
              void queryClient.invalidateQueries({ queryKey: roleHistoryQueryKey(role.id) });
              void queryClient.invalidateQueries({ queryKey: ['rbac', 'role-holders', role.id] });
              onSaved();
            }}
          />
        </>
      )}
    </div>
  );
}

function RolesPermissionsScreen() {
  const { currentTenant } = useTenant();
  const tenantId = currentTenant?.tenant_id ?? null;
  const [params, setParams] = useSearchParams();
  const queryClient = useQueryClient();
  const [duplicating, setDuplicating] = useState<number | null>(null);
  // RBAC-3.2: 'new' opens the details dialog empty; an AccountRole opens it to rename that role.
  const [editing, setEditing] = useState<AccountRole | 'new' | null>(null);
  const [deleting, setDeleting] = useState<AccountRole | null>(null);
  const [adopting, setAdopting] = useState<number | null>(null);
  const [reverting, setReverting] = useState(false);
  // RBAC-3.7: one draft per role, shared by the Roles and By capability tabs. RBAC-3.6's dialog saves it.
  const [drafts, setDrafts] = useState<Record<number, string[]>>({});
  const setDraft = (roleId: number, next: string[] | null) =>
    setDrafts((d) => {
      const rest = { ...d };
      delete rest[roleId];
      return next === null ? rest : { ...rest, [roleId]: next };
    });
  const refetch = () => {
    void queryClient.invalidateQueries({ queryKey: ['rbac', 'account-roles'] });
    void queryClient.invalidateQueries({ queryKey: ['rbac', 'role-adoption'] });
    // RBAC-8.2: every edit made here is a new row in Recent changes.
    void queryClient.invalidateQueries({ queryKey: roleAuditQueryKey(tenantId) });
  };

  const { data, isLoading, error } = useQuery({
    queryKey: ['rbac', 'account-roles', tenantId],
    queryFn: fetchAccountRoles,
    enabled: !!tenantId,
    staleTime: 60 * 1000,
  });

  const groups = useMemo(() => groupAccountRoles(data ?? []), [data]);
  const current = selectedRole(groups, params.get('role'));
  const selectId = (id: number) =>
    setParams((p) => {
      p.set('role', String(id));
      return p;
    }, { replace: true });
  const select = (r: AccountRole) => selectId(r.id);
  const view = params.get('view') === 'capability' ? 'capability' : params.get('view') === 'changes' ? 'changes' : 'roles';
  const setView = (v: string) =>
    setParams((p) => {
      if (v === 'capability' || v === 'changes') p.set('view', v);
      else p.delete('view');
      return p;
    }, { replace: true });
  const selectCapability = (id: string) =>
    setParams((p) => {
      p.set('cap', id);
      return p;
    }, { replace: true });
  const orderedRoles = useMemo(() => [...groups.own, ...groups.joules], [groups]);

  // RBAC-3.4: only an account's own role can be an adoption; Joules roles never ask.
  const ownSelected = current && !current.isGlobal ? current.id : null;
  const { data: adoptionStatus } = useQuery({
    queryKey: ['rbac', 'role-adoption', tenantId, ownSelected],
    queryFn: () => fetchRoleAdoptionStatus(ownSelected as number),
    enabled: !!tenantId && ownSelected !== null,
    staleTime: 30 * 1000,
  });
  const adoption = adoptionStatus?.adopted && adoptionStatus.roleId === ownSelected ? adoptionStatus : null;

  if (isLoading || !tenantId) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-xl border border-border bg-card p-6 text-sm text-foreground">
        The roles for this account could not be loaded. {error instanceof Error ? error.message : ''}
      </div>
    );
  }

  return (
    <Tabs value={view} onValueChange={setView} className="space-y-4">
      <TabsList>
        <TabsTrigger value="roles">Roles</TabsTrigger>
        <TabsTrigger value="capability">By capability</TabsTrigger>
        <TabsTrigger value="changes">Recent changes</TabsTrigger>
      </TabsList>
      <TabsContent value="changes" className="mt-0">
        <RoleChangeLog tenantId={tenantId} />
      </TabsContent>
      <TabsContent value="capability" className="mt-0">
        <div className="overflow-hidden rounded-xl border border-border bg-card">
          <CapabilityLookup
            roles={orderedRoles}
            drafts={drafts}
            onDraftChange={setDraft}
            capabilityId={params.get('cap')}
            onSelectCapability={selectCapability}
          />
        </div>
      </TabsContent>
      <TabsContent value="roles" className="mt-0">
        <div className="grid overflow-hidden rounded-xl border border-border bg-card md:grid-cols-[280px_minmax(0,1fr)]">
          <RoleListNav groups={groups} current={current} onSelect={select} onNew={() => setEditing('new')} />
          <section aria-label="Selected role" className="min-w-0 p-6">
            {current ? (
              <RoleSummary
                role={current}
                adoption={adoption}
                onAdopt={() => setAdopting(current.id)}
                onRevert={() => setReverting(true)}
                onDuplicate={() => setDuplicating(current.id)}
                onRename={() => setEditing(current)}
                onDelete={() => setDeleting(current)}
                draft={drafts[current.id] ?? current.permissions}
                onDraftChange={(next) => setDraft(current.id, next)}
                onSaved={refetch}
              />
            ) : (
              <p className="text-sm text-muted-foreground">There are no roles to show.</p>
            )}
          </section>
          <DuplicateRoleDialog
            open={duplicating !== null}
            sourceRoleId={duplicating}
            onOpenChange={(open) => !open && setDuplicating(null)}
            onDuplicated={(result) => {
              setDuplicating(null);
              selectId(result.roleId);
              refetch();
            }}
          />
          <AdoptRoleDialog
            open={adopting !== null}
            sourceRoleId={adopting}
            onOpenChange={(open) => !open && setAdopting(null)}
            onAdopted={(result) => {
              setAdopting(null);
              selectId(result.roleId);
              refetch();
            }}
          />
          <RevertAdoptionDialog
            open={reverting}
            status={adoption}
            copyName={current?.name ?? ''}
            onOpenChange={setReverting}
            onReverted={(result) => {
              setReverting(false);
              // The copy is gone: drop its status rather than refetch it, which would ask about a deleted
              // role (P0002, seen as a 500 in the RBAC-3.4 browser drive).
              queryClient.removeQueries({ queryKey: ['rbac', 'role-adoption'] });
              selectId(result.sourceRoleId);
              void queryClient.invalidateQueries({ queryKey: ['rbac', 'account-roles'] });
            }}
          />
          <RoleDetailsDialog
            open={editing !== null}
            role={editing === 'new' || editing === null ? null : editing}
            onOpenChange={(open) => !open && setEditing(null)}
            onSaved={(result) => {
              setEditing(null);
              selectId(result.roleId);
              refetch();
            }}
          />
          <DeleteRoleDialog
            open={deleting !== null}
            role={deleting ? { id: deleting.id, permissionCount: deleting.permissions.length } : null}
            onOpenChange={(open) => !open && setDeleting(null)}
            onDeleted={() => {
              setDeleting(null);
              setParams((p) => {
                p.delete('role');
                return p;
              }, { replace: true });
              refetch();
            }}
          />
        </div>
      </TabsContent>
    </Tabs>
  );
}

/** The route element. Decides access before anything is fetched. */
export default function RolesPermissionsPage() {
  const { permissions, loading } = usePermissions();
  const access = rolesScreenAccess({ loading, held: permissions });

  return (
    <div className="container mx-auto py-10">
      <PageHeader />
      {access === 'loading' ? (
        <div className="flex h-64 items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : access === 'denied' ? (
        <RolesNoAccess />
      ) : (
        <RolesPermissionsScreen />
      )}
    </div>
  );
}
